import { useCallback, useEffect, useRef, useState } from "react";

// 15-key diatonic piano (C4 → C6) used by Piano Mastery lessons and Song Performance.
const NOTE_NAMES = ["C", "D", "E", "F", "G", "A", "B", "C", "D", "E", "F", "G", "A", "B", "C"];
const SEMITONES = [0, 2, 4, 5, 7, 9, 11, 12, 14, 16, 17, 19, 21, 23, 24];
const KEY_BINDINGS = ["a", "w", "s", "e", "d", "f", "t", "g", "y", "h", "u", "j", "k", "o", "l"];
const BASE_FREQ = 261.63; // C4

export const keyFreq = (index) => BASE_FREQ * Math.pow(2, SEMITONES[index] / 12);

export function PianoKeyboard({ engine, onPlay, litKeys = [], petrifiedKeys = [], disabled = false, compact = false }) {
  const [pressed, setPressed] = useState(null);
  const ctxRef = useRef(null);

  const ensureCtx = () => {
    if (engine?.current?.ensureCtx) return engine.current.ensureCtx();
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  };

  const strike = useCallback((index) => {
    if (disabled) return;
    const ctx = ensureCtx();
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = "triangle";
    o.frequency.value = keyFreq(index);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(0.4, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.9);
    const f = ctx.createBiquadFilter();
    f.type = "lowpass"; f.frequency.value = 3200;
    o.connect(f); f.connect(g); g.connect(engine?.current?.master || ctx.destination);
    o.start(t); o.stop(t + 1);
    setPressed(index);
    setTimeout(() => setPressed((p) => (p === index ? null : p)), 160);
    onPlay?.(index);
  }, [disabled, engine, onPlay]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.repeat || e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      const i = KEY_BINDINGS.indexOf(e.key.toLowerCase());
      if (i >= 0) strike(i);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [strike]);

  return (
    <div className={`grid grid-cols-[repeat(15,1fr)] gap-1 select-none ${compact ? "h-28" : "h-36 sm:h-44"}`} data-testid="piano-keyboard">
      {NOTE_NAMES.map((name, i) => {
        const stone = petrifiedKeys.includes(i);
        const lit = litKeys.includes(i) || pressed === i;
        return (
          <button
            key={i}
            onPointerDown={() => strike(i)}
            disabled={disabled}
            aria-label={`Play ${name}${i < 7 ? 4 : i < 14 ? 5 : 6}`}
            className="rounded-b-lg border-t-2 font-mono text-[9px] uppercase flex flex-col items-center justify-end pb-1.5 transition-all duration-100 active:translate-y-0.5 disabled:cursor-not-allowed"
            style={{
              borderTopColor: stone ? "#3f3f46" : lit ? "#FFB800" : "#A855F7",
              background: stone
                ? "linear-gradient(180deg,#3a3a42,#26262c)"
                : lit
                  ? "linear-gradient(180deg,#7c3aed,#4c1d95)"
                  : "linear-gradient(180deg,#1c1c24,#121218)",
              boxShadow: lit && !stone ? "0 0 18px rgba(168,85,247,0.55), inset 0 0 12px rgba(255,184,0,0.25)" : "none",
              filter: stone ? "grayscale(1) brightness(0.65)" : "none",
            }}
            data-testid={`piano-key-${i}`}
          >
            <span style={{ color: stone ? "#52525b" : lit ? "#FFE9A8" : "#71717a" }}>{name}</span>
            <span className="text-[8px] text-ice/25 hidden sm:block">{KEY_BINDINGS[i]}</span>
          </button>
        );
      })}
    </div>
  );
}
